/**
 * Component for horizontal headers of links
 *
 * @module components/NavHorizontal
 */

import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { AriaRoles, messages } from '../../../constants/AccessibilityConstants';
import QATags from '../../../constants/QATags';
import { createBEM } from '../../../lib/utils/css';

import './NavHorizontal.scss';

class NavHorizontal extends React.Component {
  static propTypes = {
    links: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        isActive: PropTypes.bool,
        isHidden: PropTypes.bool,
        isDisabled: PropTypes.bool,
        type: PropTypes.string,
        qaTag: PropTypes.oneOf(values(QATags)),
        onClick: PropTypes.func,
        // itemWrapper used to pass in a wrapper element such as a Coachmark
        // Use itemWrapperProps.filterType to specify which item should be wrapped
        itemWrapper: PropTypes.node,
        itemWrapperProps: PropTypes.object,
        itemWrapperOnClose: PropTypes.object,
      }),
    ).isRequired,
    onLinkClicked: PropTypes.func.isRequired,
    navType: PropTypes.string,
    isBackgroundDark: PropTypes.bool,
    isWhiteText: PropTypes.bool,
    ariaLabel: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.link = React.createRef();
  }

  onClick = (event, url, logAction, isDisabled) => {
    event.preventDefault();

    if (isDisabled) {
      return;
    }

    this.link.current.focus();
    this.link.current.blur();
    this.props.onLinkClicked(url, logAction);
  };

  onLinkClick = link => {
    return e => {
      if (link.onClick) {
        link.onClick();
      }

      this.onClick(e, link.url, link.logAction, link.isDisabled);
    };
  };

  renderLink = (link, i) => {
    const itemLinkClasses = createBEM('NavHorizontal__item__link', {
      active: link.isActive,
      hidden: link.isHidden,
    });

    let renderedLink = (
      <a
        href={link.url}
        ref={this.link}
        className={itemLinkClasses}
        onClick={this.onLinkClick(link)}
        data-qa={link.qaTag}
      >
        {link.label}
        {link.isActive && (
          <span className='a11y--visuallyHidden'>
            <FormattedMessage {...messages.currentSection} />
          </span>
        )}
      </a>
    );

    if (link.type === 'scrollTo') {
      renderedLink = (
        <button
          type='button'
          ref={this.link}
          className={itemLinkClasses}
          onClick={link.onClick}
          data-qa={link.qaTag}
          role={AriaRoles.MENUITEM}
        >
          {link.label}
        </button>
      );
    }
    if (link.type === 'button') {
      renderedLink = (
        <button
          type='button'
          ref={this.link}
          className={itemLinkClasses}
          onClick={this.onLinkClick(link)}
          data-qa={link.qaTag}
          role={AriaRoles.MENUITEM}
        >
          {link.label}
        </button>
      );
    }
    if (link.type === 'button' && this.props.navType === 'tabs') {
      renderedLink = (
        <button
          type='button'
          ref={this.link}
          className={itemLinkClasses}
          onClick={this.onLinkClick(link)}
          data-qa={link.qaTag}
          role={AriaRoles.TAB}
          id={'tab-' + link.url}
          aria-controls={'tabpanel-' + link.url}
          aria-selected={link.isActive}
        >
          {link.label}
        </button>
      );
    }
    if (link.itemWrapper) {
      return (
        <li className='NavHorizontal__item' key={i}>
          <link.itemWrapper {...link.itemWrapperProps} onClose={link.itemWrapperOnClose}>
            {renderedLink}
          </link.itemWrapper>
        </li>
      );
    }

    return (
      <li className='NavHorizontal__item' key={i}>
        {renderedLink}
      </li>
    );
  };

  render() {
    const classes = createBEM('NavHorizontal', {
      dark: this.props.isBackgroundDark,
      white: this.props.isWhiteText,
    });
    return (
      <ul
        className={classes}
        role={this.props.navType === 'tabs' ? AriaRoles.TABLIST : AriaRoles.NAVIGATION}
        aria-label={this.props.ariaLabel}
      >
        {this.props.links.map((link, i) => {
          return this.renderLink(link, i);
        })}
      </ul>
    );
  }
}

export default NavHorizontal;
